"use client";
import { IconName } from "@zapier/zinnia-icons";

import {
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  BetaMenuTriggerSeparator,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VisuallyHidden,
} from "@zapier/design-system";

import { useEffect, useRef } from "react";

import styles from "./KebabMenu.module.css";
import KebabMenuItem from "./KebabMenuItem";

export interface KebabMenuItem {
  /** When false, menu stays open after this item is selected (e.g. while a redirect is in progress). */
  shouldCloseOnSelect?: boolean;
  disabled?: boolean;
  divider?: boolean;
  hasWarning?: boolean;
  href?: string;
  icon?: IconName;
  label: string;
  loading?: boolean;
  onClick?: () => void;
  tooltipText?: string;
}

/**
 * Loading affordance for the menu's options. `isLoading` and its `label` are
 * bundled into one prop so they're coupled at the type level: a caller can't wire
 * a loading state without also providing the accessible label, which would render
 * a silent, unlabeled loading row — the exact barrier this menu is meant to remove.
 */
export interface KebabMenuLoadingState {
  isLoading: boolean;
  /** Localized text for the loading state, e.g. "Options loading…". */
  label: string;
}

interface KebabMenuProps {
  // visible hover tooltip, and the aria-label fallback when buttonAriaLabel is unset
  buttonLabel: string;
  // optional distinct aria-label (e.g. one that includes the asset name) exposed
  // to assistive tech only — the visible tooltip still uses buttonLabel
  buttonAriaLabel?: string;
  // will be used as the aria-label for the menu
  menuLabel: string;
  menuItems: KebabMenuItem[];
  onOpenMenu?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  size?: "small" | "medium";
  // When provided and isLoading is true, the open menu shows a single announced
  // "loading" row instead of its items, so users know the options are still
  // resolving (e.g. while folder permissions are being fetched).
  loadingState?: KebabMenuLoadingState;
}

/**
 * Off-screen polite live region that announces the loading label to assistive tech
 * without moving focus (WCAG 4.1.3 Status Messages). Rendered inside the popover
 * (react-aria aria-hides everything OUTSIDE an open popover, which would mute a region
 * placed there) whenever a caller opts into loading.
 *
 * The status text is written IMPERATIVELY via a ref in an effect — not held in React
 * state — so there's no prop→state mirror and no extra same-value re-render. The <div>
 * commits EMPTY (no JSX children), then the effect populates it on the next commit, so
 * every mount produces a real "" → label content change. A polite live region announces
 * on content change (not on mount-with-content), so this reliably fires on:
 *   - open-then-flip-to-loading — the region is already mounted; the effect re-runs when
 *     `isLoading` flips true; and
 *   - reopen-while-still-loading — the popover (and this region) remounts empty and the
 *     mount effect paints the label.
 * Both are real folder-menu paths. This is the same imperative-live-region technique
 * react-aria's own LiveAnnouncer uses.
 */
const LoadingAnnouncer = ({
  isLoading,
  label,
}: {
  isLoading: boolean;
  label: string;
}) => {
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (statusRef.current) {
      statusRef.current.textContent = isLoading ? label : "";
    }
  }, [isLoading, label]);

  return (
    <VisuallyHidden>
      <div
        ref={statusRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />
    </VisuallyHidden>
  );
};

const KebabMenu = ({
  buttonLabel,
  buttonAriaLabel,
  menuItems,
  menuLabel,
  onOpenMenu,
  onOpenChange,
  size = "small",
  loadingState,
}: KebabMenuProps) => {
  const triggerReference = useRef<HTMLButtonElement>(null);
  const focusRestoreFrame = useRef<number | null>(null);
  const isLoading = loadingState?.isLoading ?? false;
  const loadingLabel = loadingState?.label ?? "";

  const cancelFocusRestore = () => {
    if (focusRestoreFrame.current !== null) {
      cancelAnimationFrame(focusRestoreFrame.current);
      focusRestoreFrame.current = null;
    }
  };

  useEffect(() => cancelFocusRestore, []);

  return (
    <BetaMenuTriggerRoot
      onOpenChange={(open) => {
        onOpenChange?.(open);

        if (open) {
          // Reopening before the previous close's restore fires would otherwise
          // pull focus out of the fresh popover and collapse it again.
          cancelFocusRestore();
          onOpenMenu?.();
          return;
        }

        // Double rAF until BetaMenuTriggerRoot restores focus on close (DS-3814).
        // Overlay teardown in DS 10.17+ completes after the first frame.
        focusRestoreFrame.current = requestAnimationFrame(() => {
          focusRestoreFrame.current = requestAnimationFrame(() => {
            focusRestoreFrame.current = null;
            triggerReference.current?.focus();
          });
        });
      }}
    >
      <Tooltip content={buttonLabel}>
        <IconButton
          ref={triggerReference}
          aria-label={buttonAriaLabel ?? buttonLabel}
          variant="ghost"
          name="navMoreVert"
          size={size}
        />
      </Tooltip>
      {/*
       * "bottom end" right-aligns the menu with the kebab regardless of menu
       * width. A fixed crossOffset of -130 only approximated this for wide
       * multi-item menus and pushed short menus (e.g. a single-item menu)
       * fully clear of the trigger.
       */}
      <BetaMenuTriggerPopover placement="bottom end">
        <BetaMenuTriggerList
          aria-label={menuLabel}
          renderEmptyState={
            isLoading
              ? () => (
                  <span className={styles.menuLoading}>
                    {/*
                      The DS Spinner always renders its own role="status" ("Loading"). Hide it
                      from assistive tech so it doesn't compete with the status announcer and
                      the visible label below; the spinner here is purely decorative.
                    */}
                    <span
                      aria-hidden="true"
                      className={styles.menuLoadingSpinner}
                    >
                      <Spinner size="small" />
                    </span>
                    <Text type="Body3">{loadingLabel}</Text>
                  </span>
                )
              : undefined
          }
        >
          {/*
            While loading, render an empty collection so RAC shows renderEmptyState (a single
            "Options loading…" row) instead of a list of dead, disabled items.
          */}
          {isLoading
            ? []
            : menuItems.map(
                (
                  {
                    label,
                    onClick,
                    href,
                    hasWarning,
                    disabled,
                    tooltipText,
                    icon,
                    divider,
                    shouldCloseOnSelect,
                    loading,
                  }: KebabMenuItem,
                  index,
                ) => {
                  if (divider) {
                    return (
                      <BetaMenuTriggerSeparator key={`divider-${index}`} />
                    );
                  }

                  return (
                    <KebabMenuItem
                      key={label}
                      shouldCloseOnSelect={shouldCloseOnSelect}
                      disabled={disabled}
                      hasWarning={hasWarning}
                      href={href}
                      label={label}
                      loading={loading}
                      onClick={onClick}
                      tooltipText={tooltipText}
                      icon={icon}
                    />
                  );
                },
              )}
        </BetaMenuTriggerList>
        {/*
          Live-region announcer sits AFTER the menu list: a role="status" is invalid as a
          child of role="menu", and placing it before the list disturbs the menu's initial
          focus. Rendered whenever a caller opts into loading so it remounts with the popover
          on every reopen (see LoadingAnnouncer for why that matters).
        */}
        {loadingState ? (
          <LoadingAnnouncer isLoading={isLoading} label={loadingLabel} />
        ) : undefined}
      </BetaMenuTriggerPopover>
    </BetaMenuTriggerRoot>
  );
};

export default KebabMenu;
